<template>
  <HeroSection
    title="Professional Development"
    subtitle="Bite-sized microlearning to empower creators, innovators, and
problem solvers."
    backgroundColor="#4C4DDC"
    illustration-image="/images/pd-hero-illustration.svg"
    backgroundImage="/images/pd-hero.svg"
    backgroundImageMobile="/images/professional-development-bg.svg"
  />

  <PDMobileFilterActions
    v-if="isMobile"
    :search-query="searchQuery"
    :selected-sort="currentSort"
    :is-filter-active="hasActiveFilters"
    @update:search-query="onSearchQueryUpdate"
    @update:selected-sort="currentSort = $event"
    @search="handleSearchChange"
    @clear-search="clearSearch"
    @sort-changed="handleSortChange"
    @filter-clicked="handleMobileFilterClick"
  />

  <div class="pd-content-container">
    <!-- Sidebar Filter -->
    <div class="pd-sidebar" :style="sidebarStyle">
      <PDFilterSidebar
        :selected-subjects="selectedSubjects"
        :selected-grades="selectedGrades"
        :selected-domains="selectedDomains"
        @filter-change="handleFilterChange"
        @reset-filters="resetFilters"
        @mode-change="handleFilterModeChange"
      />
    </div>

    <!-- Main Content -->
    <div class="pd-main-content" :style="mainContentStyle">
      <!-- Search and Sort -->
      <SearchAndFiltersSection
        v-if="!isMobile"
        :search-query="searchQuery"
        :selected-sort="currentSort"
        :sort-options="pdSortOptions"
        :selected-chips="selectedChips"
        :variant="'purple'"
        :hide-locale="true"
        :hide-chips="false"
        search-placeholder="Search professional development modules"
        search-aria-label="Search professional development modules"
        @update:search-query="onSearchQueryUpdate"
        @update:selected-sort="currentSort = $event"
        @search="handleSearchChange"
        @clear-search="clearSearch"
        @sort-changed="handleSortChange"
        @clear-filter="clearFilter"
        @clear-all-filters="clearAllFilters"
      />

      <!-- Search results info -->
      <p v-if="filters.search" class="search-results-info">
        Showing {{ lessons.length }} results of
        <b>{{ filters.search }}</b>
      </p>

      <!-- Lesson Cards -->
      <div v-if="isLoading" class="loading-container">
        <div class="loader"></div>
        <p>Loading lessons...</p>
      </div>

      <div v-else-if="lessons.length === 0" class="no-lessons">
        <ErrorState
          state="notFound"
          :title="emptyStateTitle"
          :message="notFoundMessage"
        />
      </div>

      <div v-else class="lesson-cards-container">
        <ProfessionalDevelopmentLessonCard
          v-for="lesson in lessons"
          :key="lesson.id"
          :lesson="lesson"
          :is-logged="isAuthenticated"
          :can-like="isAuthenticated"
          @card-clicked="viewLesson(lesson.slug)"
          @update-liked-status="handleLikedStatusUpdate"
        />
      </div>

      <!-- Pagination -->
      <BasePagination
        v-if="totalPages > 1"
        v-model="currentPage"
        :total-pages="totalPages"
        @update:model-value="changePage"
      />
    </div>
  </div>

  <PDMobileFilterBottomSheet
    v-if="isMobile"
    v-model:visible="showMobileFilterSheet"
    :selected-subjects="selectedSubjects"
    :selected-grades="selectedGrades"
    :selected-domains="selectedDomains"
    @filter-change="handleFilterChange"
    @reset-filters="resetFilters"
    @close="handleMobileFilterClose"
  />
</template>

<script setup>
import HeroSection from "@/components/common/HeroSection.vue";
import BasePagination from "@/components/common/BasePagination.vue";
import SearchAndFiltersSection from "@/components/maker-cart/catalog/custom/SearchAndFiltersSection.vue";
import PDFilterSidebar from "@/components/professional-development/PDFilterSidebar.vue";
import PDMobileFilterActions from "@/components/professional-development/PDMobileFilterActions.vue";
import PDMobileFilterBottomSheet from "@/components/professional-development/PDMobileFilterBottomSheet.vue";
import ProfessionalDevelopmentLessonCard from "@/components/professional-development/ProfessionalDevelopmentLessonCard.vue";
import { useUrlQuerySync } from "@/composeables/useUrlQuerySync";
import cookiesService from "@/services/cookiesService";
import professionalDevelopmentService from "@/services/professionalDevelopmentService";
import { debounce } from "lodash";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import ErrorState from "@/components/common/ErrorState.vue";

const router = useRouter();
const isLoading = ref(true);
const lessons = ref([]);
const totalPages = ref(0);
const currentPage = ref(1);
const searchQuery = ref("");
const currentSort = ref("id");

const isAuthenticated = ref(false);
const emptyStateTitle = computed(() => {
  const hasSearch =
    searchQuery.value && String(searchQuery.value).trim() !== "";
  const hasFilters =
    (selectedSubjects.value && selectedSubjects.value.length > 0) ||
    (selectedGrades.value && selectedGrades.value.length > 0) ||
    (selectedDomains.value && selectedDomains.value.length > 0);

  if (hasSearch && hasFilters) {
    return `No results found for '${searchQuery.value}' with current filters`;
  } else if (hasSearch) {
    return `Oh no! We didn't find any results for '${searchQuery.value}'`;
  } else if (hasFilters) {
    return "No results match your current filters";
  } else {
    return "No professional development courses available";
  }
});

const notFoundMessage = computed(() => {
  const hasSearch =
    searchQuery.value && String(searchQuery.value).trim() !== "";
  const hasFilters =
    (selectedSubjects.value && selectedSubjects.value.length > 0) ||
    (selectedGrades.value && selectedGrades.value.length > 0) ||
    (selectedDomains.value && selectedDomains.value.length > 0);

  if (hasSearch && hasFilters) {
    return "Try adjusting your search terms or clearing some filters to see more results.";
  } else if (hasSearch) {
    return "Try different search terms or clear filters to see more results.";
  } else if (hasFilters) {
    return "No courses match your current filters. Try clearing some filters to see more results.";
  } else {
    return "No professional development courses are currently available. Please check back later.";
  }
});

// Selected filters
const selectedSubjects = ref([]);
const selectedGrades = ref([]);
const selectedDomains = ref([]);
const hasActiveFilters = computed(
  () =>
    selectedSubjects.value.length > 0 ||
    selectedGrades.value.length > 0 ||
    selectedDomains.value.length > 0
);

// Sort options formatted for SearchAndFiltersSection
const pdSortOptions = [
  {
    value: "id",
    text: "<b><strong>Published Date</strong></b>",
  },
  {
    value: "like_count",
    text: "<b><strong>Popularity</strong></b>",
  },
];

// Responsive sidebar/content width (matching MakerCart pattern)
const windowWidth = ref(window.innerWidth);
const isFilterExpanded = ref(false);

const handleFilterModeChange = (isExpanded) => {
  isFilterExpanded.value = isExpanded;
};

const isMobile = computed(() => windowWidth.value <= 768);
const showMobileFilterSheet = ref(false);

const handleMobileFilterClick = () => {
  showMobileFilterSheet.value = true;
};

const handleMobileFilterClose = () => {
  showMobileFilterSheet.value = false;
};

const sidebarStyle = computed(() => {
  let compactWidth, expandedWidth;

  if (windowWidth.value > 1600) {
    compactWidth = "10%";
    expandedWidth = "18%";
  } else if (windowWidth.value > 768) {
    compactWidth = "15%";
    expandedWidth = "25%";
  } else {
    compactWidth = "0%";
    expandedWidth = "100%";
  }

  return isFilterExpanded.value
    ? { width: expandedWidth }
    : { width: compactWidth };
});

const mainContentStyle = computed(() => {
  let compactWidth, expandedWidth, compactPadding, expandedPadding;

  if (windowWidth.value > 1200) {
    compactWidth = "90%";
    expandedWidth = "82%";
    compactPadding = "0px 30px";
    expandedPadding = "0px 20px";
  } else if (windowWidth.value > 768) {
    compactWidth = "85%";
    expandedWidth = "75%";
    compactPadding = "0px 20px";
    expandedPadding = "0px 15px";
  } else {
    compactWidth = "100%";
    expandedWidth = "100%";
    compactPadding = "0px 16px";
    expandedPadding = "0px 16px";
  }

  return isFilterExpanded.value
    ? { padding: expandedPadding, width: expandedWidth }
    : { padding: compactPadding, width: compactWidth };
});

const handleResize = () => {
  windowWidth.value = window.innerWidth;
};

const filters = ref({
  search: "",
  page: 1,
  limit: 9,
  sort_by: "",
  order: "",
  domain: [],
  subject: [],
  grade: [],
});

// Setup URL query sync with filters
useUrlQuerySync({
  filters,
  excludeDefaults: [{ page: 1 }, { limit: 9 }],
});

// Watch for changes in filters from URL and update selected filter refs
watch(
  () => filters.value.subject,
  (newSourceValue) => {
    let valuesToStore = [];
    if (Array.isArray(newSourceValue)) {
      valuesToStore = newSourceValue.map(String).filter(Boolean);
    } else if (
      newSourceValue !== null &&
      newSourceValue !== undefined &&
      String(newSourceValue).trim() !== ""
    ) {
      valuesToStore = [String(newSourceValue)]; // Ensure it's a string
    }
    selectedSubjects.value = valuesToStore;
  },
  { immediate: true, deep: true }
);

watch(
  () => filters.value.grade,
  (newSourceValue) => {
    let valuesToStore = [];
    if (Array.isArray(newSourceValue)) {
      valuesToStore = newSourceValue.map(String).filter(Boolean);
    } else if (
      newSourceValue !== null &&
      newSourceValue !== undefined &&
      String(newSourceValue).trim() !== ""
    ) {
      valuesToStore = [String(newSourceValue)];
    }
    selectedGrades.value = valuesToStore;
  },
  { immediate: true, deep: true }
);

watch(
  () => filters.value.domain,
  (newSourceValue) => {
    let valuesToStore = [];
    if (Array.isArray(newSourceValue)) {
      valuesToStore = newSourceValue.map(String).filter(Boolean);
    } else if (
      newSourceValue !== null &&
      newSourceValue !== undefined &&
      String(newSourceValue).trim() !== ""
    ) {
      valuesToStore = [String(newSourceValue)];
    }
    selectedDomains.value = valuesToStore;
  },
  { immediate: true, deep: true }
);

// Sync currentPage with filters.page
watch(
  () => filters.value.page,
  (newPage) => {
    currentPage.value = newPage || 1;
  },
  { immediate: true }
);

onMounted(async () => {
  // Check authentication status
  const token = await cookiesService.getCookie("authToken");
  if (token) {
    isAuthenticated.value = true;
  }

  // Add resize listener for responsive sidebar
  window.addEventListener("resize", handleResize);

  // Fetch lessons after authentication check
  await fetchLessons();
});

// Sync search query with filters.search
watch(
  () => filters.value.search,
  (newValue) => {
    searchQuery.value = newValue || "";
  }
);

// Sync sort options with filters
watch(
  [() => filters.value.sort_by, () => filters.value.order],
  ([sortBy, order]) => {
    if (sortBy === "id" && order === "desc") {
      currentSort.value = "id";
    } else if (sortBy === "like_count" && order === "desc") {
      currentSort.value = "like_count";
    }
  },
  { immediate: true }
);

// Calculate which chips to display
const selectedChips = computed(() => {
  const chips = [];

  const format = (key, label, values) => {
    if (!values || values.length === 0) return;

    let text;
    if (values.length === 1) {
      text = values[0];
    } else {
      text = `<span class="chip-label">${label}:</span> <span class="chip-values" style="margin-left: 2px;"> ${values.join(
        " / "
      )}</span>`;
    }
    chips.push({ key, text });
  };

  format("grade", "Grades", selectedGrades.value);
  format("subject", "Subjects", selectedSubjects.value);
  format("domain", "Tech Domains", selectedDomains.value);

  return chips;
});

const fetchLessons = async () => {
  isLoading.value = true;
  try {
    const response = await professionalDevelopmentService.getAll(filters.value);
    if (response.status === 200) {
      lessons.value = response.data.data.rows.map((lesson) => ({
        ...lesson,
        isLiked: lesson.isLiked || false,
        like_count: lesson.like_count || 0,
      }));
      totalPages.value = response.data.data.total_page;
    }
  } catch (error) {
    console.error("Error fetching lessons:", error);
    lessons.value = [];
  } finally {
    isLoading.value = false;
  }
};

const handleSearchChange = debounce(() => {
  filters.value.search = searchQuery.value;
  filters.value.page = 1;
  currentPage.value = 1;
  fetchLessons();
}, 300);

const onSearchQueryUpdate = (value) => {
  searchQuery.value = value;
  handleSearchChange();
};

const clearSearch = () => {
  searchQuery.value = "";
  filters.value.search = "";
  filters.value.page = 1;
  currentPage.value = 1;
  fetchLessons();
};

const handleLikedStatusUpdate = ({ id, liked, likeCount }) => {
  const lessonIndex = lessons.value.findIndex((lesson) => lesson.id === id);
  if (lessonIndex === -1) {
    return;
  }

  lessons.value[lessonIndex].isLiked = liked;
  lessons.value[lessonIndex].like_count = likeCount;
};

const handleSortChange = (sortValue) => {
  // sortValue may come as a string directly or from the original change event
  const value = sortValue?.value || sortValue || currentSort.value;
  currentSort.value = value;
  switch (value) {
    case "id":
      filters.value.sort_by = "id";
      filters.value.order = "desc";
      break;
    case "like_count":
      filters.value.sort_by = "like_count";
      filters.value.order = "desc";
      break;
    default:
      filters.value.sort_by = "";
      filters.value.order = "";
      break;
  }
  filters.value.page = 1;
  currentPage.value = 1;
  fetchLessons();
};

const clearFilter = (filterKey) => {
  if (filterKey === "grade") {
    selectedGrades.value = [];
    filters.value.grade = [];
  } else if (filterKey === "subject") {
    selectedSubjects.value = [];
    filters.value.subject = [];
  } else if (filterKey === "domain") {
    selectedDomains.value = [];
    filters.value.domain = [];
  }

  filters.value.page = 1;
  currentPage.value = 1;
  fetchLessons();
};

const clearAllFilters = () => {
  resetFilters();
};

const handleFilterChange = (filterData) => {
  selectedSubjects.value = filterData.subjects;
  selectedGrades.value = filterData.grades;
  selectedDomains.value = filterData.domains;
  filters.value.domain = filterData.domains;
  filters.value.subject = filterData.subjects;
  filters.value.grade = filterData.grades;
  filters.value.page = 1;
  currentPage.value = 1;
  fetchLessons();
};

const resetFilters = () => {
  selectedSubjects.value = [];
  selectedGrades.value = [];
  selectedDomains.value = [];
  filters.value.domain = [];
  filters.value.subject = [];
  filters.value.grade = [];
  filters.value.page = 1;
  currentPage.value = 1;
  fetchLessons();
};

const changePage = (page) => {
  if (page === "...") return;
  currentPage.value = page;
  filters.value.page = page;
  fetchLessons();
};

const viewLesson = (slug) => {
  router.push({
    name: "ViewProfessionalDevelopment",
    params: { slug },
  });
};

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});
</script>

<style scoped>
/* Sidebar + Content layout (matching MakerCart pattern) */
.pd-content-container {
  width: 100%;
  margin: 0 auto;
  padding: 20px 40px;
  display: flex;
  position: relative;
}

.pd-sidebar {
  transition: width 0.3s ease-in-out;
}

.pd-main-content {
  flex-grow: 1;
  transition: all 0.3s ease-in-out;
}

.search-results-info {
  margin-top: 0.5rem;
  font-size: var(--fs-medium);
  color: var(--primary-blue);
}

.lesson-cards-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
  margin-top: 1rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
  min-height: 50vh;
}

.loader {
  border: 5px solid #f3f3f3;
  border-radius: 50%;
  border-top: 5px solid var(--primary-blue);
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.no-lessons {
  text-align: center;
  padding: 3rem 0;
  color: #666;
}

@media (max-width: 768px) {
  .pd-content-container {
    flex-direction: column;
    padding: 8px;
    gap: 16px;
  }

  .pd-sidebar {
    width: 100% !important;
    order: 2;
  }

  .pd-main-content {
    width: 100% !important;
    order: 1;
    padding: 0 !important;
  }

  .lesson-cards-container {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
}
</style>
